import { SiteSettings, Currency, Language } from './types';

// Taxas de conversão com base em USD (1 USD como referência padrão da loja)
export const CURRENCY_RATES: Record<Currency, { symbol: string; rate: number; prefix: boolean }> = {
  USD: { symbol: '$', rate: 1.0, prefix: true },
  BRL: { symbol: 'R$', rate: 5.0, prefix: true },
  EUR: { symbol: '€', rate: 0.92, prefix: false },
  RUB: { symbol: '₽', rate: 90.0, prefix: false }
};

export const formatCurrency = (usdPrice: number, currency: Currency = 'USD'): string => {
  const config = CURRENCY_RATES[currency] || CURRENCY_RATES.USD;
  const converted = usdPrice * config.rate;
  const formatted = converted.toFixed(2);
  return config.prefix ? `${config.symbol} ${formatted}` : `${formatted} ${config.symbol}`;
};

export const slugify = (text: string): string => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

export const getActiveDiscountPercentage = (settings?: SiteSettings | null): number => {
  if (!settings) return 0;
  const pct = settings.globalDiscountPercentage;
  if (!pct || pct <= 0) return 0;
  
  const until = settings.globalDiscountUntil;
  if (until) {
    const untilDate = new Date(until);
    if (isNaN(untilDate.getTime()) || untilDate.getTime() < Date.now()) {
      return 0; // expired
    }
  }
  return Math.min(100, Math.max(0, pct));
};

export const getDiscountedPrice = (price: number, discountPercentage: number): number => {
  if (discountPercentage <= 0) return price;
  return price * (1 - discountPercentage / 100);
};

/**
 * Formata qualquer data/string/timestamp para o Horário de Brasília (America/Sao_Paulo - UTC-3)
 * Exemplo: "26/08/2026, 04:45:12"
 */
export const formatBrazilDateTime = (dateInput?: string | number | Date | null): string => {
  if (!dateInput) return 'Data não informada';
  
  if (typeof dateInput === 'string') {
    const trimmed = dateInput.trim();
    if (!trimmed) return 'Data não informada';

    // Se já estiver no formato brasileiro DD/MM/AAAA, HH:mm:ss
    const brPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[,\s]+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/;
    const match = trimmed.match(brPattern);
    if (match) {
      const [, day, month, year, hours, minutes, seconds] = match;
      const d = day.padStart(2, '0');
      const m = month.padStart(2, '0');
      const y = year;
      const h = (hours || '00').padStart(2, '0');
      const min = (minutes || '00').padStart(2, '0');
      const sec = (seconds || '00').padStart(2, '0');
      return `${d}/${m}/${y}, ${h}:${min}:${sec}`;
    }

    const num = Number(trimmed);
    let dateObj: Date;
    if (!isNaN(num) && num > 100000000000) {
      dateObj = new Date(num);
    } else {
      dateObj = new Date(trimmed);
    }

    if (!isNaN(dateObj.getTime())) {
      return dateObj.toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    }

    return trimmed;
  }

  try {
    const d = typeof dateInput === 'number' ? new Date(dateInput) : dateInput;
    if (isNaN(d.getTime())) return String(dateInput);
    return d.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (e) {
    return String(dateInput);
  }
};

/**
 * Retorna a data e hora atual no Horário de Brasília
 */
export const getBrazilNowString = (): string => {
  return new Date().toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};
